import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Mail, Bell, Save, RefreshCw, Check, AlertCircle,
  Ticket, ShoppingBag, CreditCard, Users
} from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailSettings = () => {
  const [settings, setSettings] = useState({
    admin_email: "",
    forward_email: "",
    send_customer_confirmation: true,
    send_admin_notification: true,
    send_payment_alerts: true
  });
  const [templates, setTemplates] = useState({
    event_welcome: {
      enabled: true,
      subject: "🎉 Your Booking is Confirmed! - {event_title}",
      body: ""
    },
    interac_instructions: {
      enabled: true,
      subject: "Payment Instructions - {event_title}",
      body: ""
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("notifications");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/admin/email-settings`),
        axios.get(`${API}/admin/email-templates`)
      ]);
      
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
      if (templatesRes.data?.templates) {
        setTemplates(prev => ({ ...prev, ...templatesRes.data.templates }));
      }
    } catch (err) {
      console.log("Using default settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.post(`${API}/admin/email-settings`, settings),
        axios.put(`${API}/admin/email-templates`, { templates })
      ]);
      toast.success("Email settings saved!");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    if (!settings.admin_email) {
      toast.error("Please enter an admin email first");
      return;
    }
    try {
      await axios.post(`${API}/admin/test-email`, { 
        email: settings.admin_email,
        type: "test"
      });
      toast.success(`Test email sent to ${settings.admin_email}`);
    } catch (err) {
      toast.error("Failed to send test email");
    }
  };

  const ToggleSwitch = ({ enabled, onChange, label, description, icon: Icon }) => (
    <div className="flex items-center justify-between py-4 border-b border-[#27272A]">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-5 h-5 text-[#D4AF37]" />}
        <div>
          <p className="text-[#F5F5F0] font-medium">{label}</p>
          {description && <p className="text-[#a1a1aa] text-sm mt-1">{description}</p>}
        </div>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          enabled ? "bg-[#D4AF37]" : "bg-[#27272A]"
        }`}
      >
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
          enabled ? "left-7" : "left-1"
        }`} />
      </button>
    </div>
  );

  const tabs = [
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "templates", label: "Email Templates", icon: Mail }
  ];

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#F5F5F0] flex items-center gap-2">
              <Mail className="w-7 h-7 text-[#D4AF37]" />
              Email & Notification Settings
            </h1>
            <p className="text-[#A1A1AA] mt-1">Configure email alerts for payments and bookings</p>
          </div>
          
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors rounded disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Save Settings
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#27272A]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "text-[#D4AF37] border-[#D4AF37]"
                  : "text-[#A1A1AA] border-transparent hover:text-[#F5F5F0]"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* Notifications Tab */}
            {activeTab === "notifications" && (
              <div className="grid lg:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#121212] border border-[#27272A] rounded-lg p-6"
                >
                  <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-[#D4AF37]" />
                    Admin Notification Emails
                  </h2>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-[#F5F5F0] font-medium mb-2">
                        Admin Email Address *
                      </label>
                      <input
                        type="email"
                        value={settings.admin_email}
                        onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
                        placeholder="admin@mixandmingleevents.com"
                        className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded focus:border-[#D4AF37] focus:outline-none"
                      />
                      <p className="text-[#a1a1aa] text-sm mt-1">
                        Receives all payment and booking notifications
                      </p>
                    </div>

                    <div>
                      <label className="block text-[#F5F5F0] font-medium mb-2">
                        Additional Notification Emails
                      </label>
                      <input
                        type="text"
                        value={settings.forward_email}
                        onChange={(e) => setSettings({ ...settings, forward_email: e.target.value })}
                        placeholder="team@company.com, manager@company.com"
                        className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded focus:border-[#D4AF37] focus:outline-none"
                      />
                      <p className="text-[#a1a1aa] text-sm mt-1">
                        Comma-separated list of additional emails
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={testEmail}
                    className="flex items-center gap-2 px-4 py-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
                  >
                    <Mail className="w-4 h-4" />
                    Send Test Email
                  </button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-[#121212] border border-[#27272A] rounded-lg p-6"
                >
                  <h2 className="text-lg font-semibold text-[#F5F5F0] mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                    Payment Alerts
                  </h2>

                  <ToggleSwitch
                    enabled={settings.send_payment_alerts}
                    onChange={(v) => setSettings({ ...settings, send_payment_alerts: v })}
                    label="Payment Received Alerts"
                    description="Get notified when a Stripe payment is completed"
                    icon={CreditCard}
                  />

                  <ToggleSwitch
                    enabled={settings.send_admin_notification}
                    onChange={(v) => setSettings({ ...settings, send_admin_notification: v })}
                    label="New Booking Alerts"
                    description="Get notified for all new event and landing page bookings"
                    icon={Ticket}
                  />

                  <ToggleSwitch
                    enabled={settings.send_customer_confirmation}
                    onChange={(v) => setSettings({ ...settings, send_customer_confirmation: v })}
                    label="Customer Confirmation Emails"
                    description="Send booking confirmation emails to customers"
                    icon={Users}
                  />

                  <div className="mt-6 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#D4AF37] font-medium">Email Notifications Active</p>
                        <p className="text-[#A1A1AA] text-sm mt-1">
                          You will receive emails for:
                        </p>
                        <ul className="text-[#A1A1AA] text-sm mt-2 space-y-1">
                          <li>• Landing page ticket purchases (Stripe)</li>
                          <li>• Event bookings</li>
                          <li>• Interac e-Transfer requests</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === "templates" && (
              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#121212] border border-[#27272A] rounded-lg p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-[#F5F5F0] flex items-center gap-2">
                      <Ticket className="w-5 h-5 text-[#D4AF37]" />
                      Booking Confirmation Email
                    </h2>
                    <ToggleSwitch
                      enabled={templates.event_welcome?.enabled !== false}
                      onChange={(v) => setTemplates({
                        ...templates,
                        event_welcome: { ...templates.event_welcome, enabled: v }
                      })}
                      label=""
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Subject Line</label>
                      <input
                        type="text"
                        value={templates.event_welcome?.subject || ""}
                        onChange={(e) => setTemplates({
                          ...templates,
                          event_welcome: { ...templates.event_welcome, subject: e.target.value }
                        })}
                        className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded focus:border-[#D4AF37] focus:outline-none"
                        placeholder="🎉 Your Booking is Confirmed! - {event_title}"
                      />
                      <p className="text-[#a1a1aa] text-xs mt-1">
                        Variables: {"{name}"}, {"{event_title}"}, {"{booking_reference}"}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-[#121212] border border-[#27272A] rounded-lg p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-[#F5F5F0] flex items-center gap-2">
                      <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
                      Interac Payment Instructions
                    </h2>
                    <ToggleSwitch
                      enabled={templates.interac_instructions?.enabled !== false}
                      onChange={(v) => setTemplates({
                        ...templates,
                        interac_instructions: { ...templates.interac_instructions, enabled: v }
                      })}
                      label=""
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-sm mb-2">Subject Line</label>
                      <input
                        type="text"
                        value={templates.interac_instructions?.subject || ""}
                        onChange={(e) => setTemplates({
                          ...templates,
                          interac_instructions: { ...templates.interac_instructions, subject: e.target.value }
                        })}
                        className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded focus:border-[#D4AF37] focus:outline-none"
                        placeholder="Payment Instructions - {event_title}"
                      />
                    </div>
                  </div>
                </motion.div>

                <div className="p-4 bg-[#0F0F0F] border border-[#27272A] rounded-lg">
                  <p className="text-[#A1A1AA] text-sm">
                    <strong className="text-[#F5F5F0]">Available Variables:</strong> Use these placeholders in your email templates:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["{name}", "{email}", "{event_title}", "{event_date}", "{event_time}", "{event_location}", "{ticket_type}", "{quantity}", "{booking_reference}", "{total_amount}"].map(v => (
                      <code key={v} className="px-2 py-1 bg-[#27272A] text-[#D4AF37] text-xs rounded">{v}</code>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default EmailSettings;
